'use client';

import React from 'react';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { payInFullPromoAppliedAction } from '@/actions/voyagePlanner/summary/payInFullPromoApply';
import BackgroundImageGradientVariative from '@/components/Background/Gradient/ImageVariative';
import CelebrationBackSearchButton from '@/components/BackSearchButton/CelebrationBackSearchButton';
import BackToTopButton from '@/components/BackToTopButton';
import BannersBox from '@/components/BannersBox';
import CabinDescriptors from '@/components/ChooseVoyage/CabinDescriptors';
import ConfidenceModuleList from '@/components/ChooseVoyage/ConfidenceModuleList';
import NewVoyageTravelParty from '@/components/ChooseVoyage/NewVoyageTravelParty';
import PreCheckOutSummaryModule from '@/components/ChooseVoyage/PreCheckOutSummaryModule';
import TravelPartyCabinDescription from '@/components/ChooseVoyage/TravelPartyMultiCabinDes';
import { ViewMoreDatesFlyout } from '@/components/ChooseVoyage/ViewMoreDates';
import CookiePreferenceLabel from '@/components/CookiePreferenceLabel';
import DisabledSailorPopup from '@/components/DisabledSailorPopup';
import PromoBannerPlace from '@/components/PromoBanner/Place';
import { getCelebrationOnBackHandler } from '@/components/Promotion/CelebrationBanner/helpers';
import ReCaptchaLabel from '@/components/ReCaptcha/ReCaptchaLabel';
import ShareButton from '@/components/ShareButton';
import WarningBox from '@/components/WarningBox';
import MuteDocTitle from '@/ducks/a11y/helpers/MuteDocTitle';
import { selectAccessKeySlice } from '@/ducks/accessKeys';
import { selectSettings } from '@/ducks/common/settings';
import { selectCelebrationsIsFlowActive } from '@/ducks/programs/celebrations/selectors';
import { selectShareUrlShareType } from '@/ducks/share/selectors';
import { selectSailingData, selectSelectedPackageFromMainPackages } from '@/ducks/voyageInfo/sailing/selectors';
import { PromoBannerPlaceId } from '@/features/promoBanners/types';
import { WarningTarget } from '@/features/warnings/types';
import withSideqikScriptForBookingFlow from '@/helpers/hoc/utility/withSideqikScriptForBookingFlow';
import { generateShareURL } from '@/helpers/social';
import { generateRedirectUrlForFullCruiseDetails } from '@/helpers/util/misc';
import withSearchParams from '@/hocs/withSearchParams';
import FlowNavigation from '@/layouts/VoyagePlanner/FlowNavigation';
import tagmanager from '@/tagmanager';

import Heading from './Heading';

import './PreCheckOut.scss';

const {
  tracker: {
    common: { trackSocialShare },
    ECommercePromotions: { measuringProductClicks, measuringProductImpressions },
    voyageDetails: { trackFullCruiseClick },
  },
  trackerConstants,
} = tagmanager;

class PreCheckOut extends React.Component {
  static onFullCruiseFlyoutClick = () => {
    trackFullCruiseClick({
      flyouts: [trackerConstants.FLYOUTS.ITINERARY_SAILING_DATES],
    });
  };

  static onShareClick = (platform) => {
    trackSocialShare({ label: platform });
  };

  createShareUrl = async (sailingData) => {
    const { shareUrlShareType } = this.props;
    const { cabinCategoryCode, id, packageCode } = sailingData;
    if (id && packageCode) {
      const fullUrl = generateShareURL(
        shareUrlShareType?.shareUrl,
        shareUrlShareType?.shareType,
        packageCode,
        id,
        cabinCategoryCode || '',
      );
      this.setState({ shareUrl: fullUrl });
    }
  };

  fullCruiseBtnClick = () => {
    trackFullCruiseClick({
      action: trackerConstants.EVENT_ACTIONS.LINK_CLICK,
    });

    const redirectURL = generateRedirectUrlForFullCruiseDetails();
    window.open(redirectURL, '_blank');
  };

  onCloseViewMoreFlyout = () => {
    // TODO: we should make available for focus on loading state. Don't use skeleton there
    const focusable = document.querySelector('.PreCheckOutSummaryModule .contextMenu button');
    if (focusable) {
      const release = MuteDocTitle.start();
      setTimeout(() => {
        focusable.focus();
        release();
      }, 350);
    }
    this.setState({ isViewMoreFlyoutShown: false });
  };

  onDisabledSailorModalClick = () => {
    const { isDisabledSailorModalOpen } = this.state;
    this.setState({ isDisabledSailorModalOpen: !isDisabledSailorModalOpen });
  };

  onSailingDateItemFlyoutClick = (sailing) => {
    this.onCloseViewMoreFlyout();

    const { actions } = this.props;
    tagmanager.tracker.voyageDetails.trackNextSailingDateFlyoutClick(sailing);

    actions.measuringProductClicks({
      labels: [trackerConstants.FLYOUTS.CHANGE_DATES],
      list: trackerConstants.LISTS.YOUR_VOYAGE,
      sailing,
    });
  };

  onSailorSericeBtnClick = () => {
    const { configServiceData } = this.props;
    this.setState({ isDisabledSailorModalOpen: false });
    const landingPageUrl = configServiceData?.ContactUsUrl;
    window.open(landingPageUrl, '_blank');
  };

  showSpecialSailingModal = (classificationCode) => {
    this.setState({ specialSailingId: classificationCode });
  };

  showViewMoreFlyout = () => {
    tagmanager.tracker.summary.trackChangeSailDateClick();
    this.setState({ isViewMoreFlyoutShown: true });
  };

  constructor(props) {
    super(props);
    this.state = {
      isDisabledSailorModalOpen: false,
      isViewMoreFlyoutShown: false,
      shareUrl: '',
      specialSailingId: null,
    };
  }

  componentDidMount() {
    const { actions, sailingData } = this.props;

    this.createShareUrl(sailingData);
    actions.payInFullPromoAppliedAction(false);
  }

  componentDidUpdate(prevProps) {
    const { actions, sailingData } = this.props;

    if (prevProps?.sailingData !== sailingData) {
      actions.measuringProductImpressions({
        list: trackerConstants.LISTS.YOUR_VOYAGE,
        sailings: [sailingData],
      });
      this.createShareUrl(sailingData);
      actions.payInFullPromoAppliedAction(false);
    }
  }

  componentWillUnmount() {
    MuteDocTitle.stop();
  }

  render() {
    const { isDisabledSailorModalOpen, isViewMoreFlyoutShown, shareUrl } = this.state;
    const { accessKeyDetails, isCelebrationFlowActive, sailingData, searchParams, selectedPackage } = this.props;
    const { accessKey = '' } = accessKeyDetails;
    const isCharter = !!searchParams.get('charterId');

    return (
      <div className={classNames('PreCheckoutPage')}>
        <FlowNavigation />

        <BackgroundImageGradientVariative src={selectedPackage?.landscapeHero?.src} />

        <main className={classNames('PreCheckoutMain', { '-hasAccessKeyBanner': accessKey })} role="main">
          <section className="PreCheckoutPage__header">
            {!isCharter && (
              <CelebrationBackSearchButton
                className="backSearch"
                onClick={getCelebrationOnBackHandler(isCelebrationFlowActive)}
              />
            )}
            <Heading
              onExitClick={getCelebrationOnBackHandler(isCelebrationFlowActive)}
              shipName={sailingData?.ship?.name}
            />
            {!isCharter && (
              <ShareButton
                isShortLink
                link={shareUrl}
                modalAlign="end"
                modalSide="bottom"
                onShare={PreCheckOut.onShareClick}
                sailingData={sailingData}
              />
            )}
          </section>
          <div className="summaryTravelPartModule">
            <div className="summaryCabinModule">
              <BannersBox className="bannersBox" />
              <WarningBox scrollTBox target={WarningTarget.preCheckoutTop} />
              <WarningBox scrollToBox target={WarningTarget.preCheckoutSecondary} />
              <PromoBannerPlace placeId={PromoBannerPlaceId.preCheckoutA} />
              <PreCheckOutSummaryModule
                fullCruiseBtnClick={this.fullCruiseBtnClick}
                isCelebrations={isCelebrationFlowActive}
                isCharter={isCharter}
                onChangeDates={this.showViewMoreFlyout}
                packageData={selectedPackage}
              />
              <CabinDescriptors />
              <div className="inMobile">
                <TravelPartyCabinDescription />
              </div>
              <PromoBannerPlace placeId={PromoBannerPlaceId.preCheckoutC} />
              <ConfidenceModuleList />
            </div>
            <NewVoyageTravelParty onDisabledSailorModalClick={this.onDisabledSailorModalClick} />
          </div>
        </main>

        {!isEmpty(selectedPackage) && (
          <ViewMoreDatesFlyout
            onItemClick={this.onSailingDateItemFlyoutClick}
            onOpenChange={(isOpen) => !isOpen && this.onCloseViewMoreFlyout()}
            open={isViewMoreFlyoutShown}
            packageData={selectedPackage}
          />
        )}
        <DisabledSailorPopup
          onDismissClick={this.onDisabledSailorModalClick}
          onSailorServiceBtnClick={this.onSailorSericeBtnClick}
          showModal={isDisabledSailorModalOpen}
        />
        <BackToTopButton />
        <ReCaptchaLabel />
        <CookiePreferenceLabel />
      </div>
    );
  }
}

function mapStateToProps(state, { searchParams }) {
  return {
    accessKeyDetails: selectAccessKeySlice(state) ?? {},
    configServiceData: selectSettings(state),
    isCelebrationFlowActive: selectCelebrationsIsFlowActive(state),

    sailingData: selectSailingData(state),
    selectedPackage: selectSelectedPackageFromMainPackages(state, searchParams),
    shareUrlShareType: selectShareUrlShareType(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        measuringProductClicks,
        measuringProductImpressions,
        payInFullPromoAppliedAction,
      },
      dispatch,
    ),
  };
}

export default withSearchParams(
  connect(mapStateToProps, mapDispatchToProps)(withSideqikScriptForBookingFlow(PreCheckOut, {})),
);
